import React from "react";
import { cn } from "@/lib/utils";
import MarstickLogoMark from "@/components/brand/MarstickLogoMark";
import { Button } from "@/components/ui/button";
import SmartMenu from "@/components/SmartMenu";
import { useTranslation } from "@/i18n";
import useMenuUserState from "@/hooks/useMenuUserState";
import { useLocation, useParams } from "react-router-dom";

export default function PublicSidebar({ lang: providedLang, collapsed = false, onPremiumClick, onNavigate, mobile = false }) {
  const { lang: routeLang } = useParams();
  const lang = providedLang || routeLang || "en";
  const { t } = useTranslation();
  const publicCopy = t("public", { defaultValue: {} });
  const userState = useMenuUserState();
  const isRTL = lang === "he" || lang === "ar";
  const location = useLocation();
  const menuScrollRef = React.useRef(null);

  React.useEffect(() => {
    const container = menuScrollRef.current;
    const activeItem = container?.querySelector('[data-menu-active="true"]');
    if (!container || !activeItem) return;

    const targetTop = activeItem.offsetTop - container.offsetTop - (container.clientHeight / 2) + (activeItem.clientHeight / 2);
    container.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
  }, [location.pathname, collapsed, lang]);

  return (
    <aside dir={isRTL ? "rtl" : "ltr"} className={cn("h-full overflow-y-auto bg-white/96 text-slate-950 backdrop-blur-2xl dark:bg-slate-950/96 dark:text-cyan-50", isRTL ? "border-l border-slate-200 dark:border-white/10" : "border-r border-slate-200 dark:border-white/10", mobile ? "w-full" : collapsed ? "w-20" : "w-64")}>
      <div className="flex h-full flex-col gap-5 p-3">
        <div className={cn("rounded-3xl bg-gradient-to-br from-ocean to-primary p-4 text-white shadow-lg shadow-primary/15", collapsed && "p-3 text-center")}>
          <div className="flex items-center gap-2">
            <MarstickLogoMark className="h-10 w-10 rounded-2xl" />
            {!collapsed && <div className="min-w-0"><p className="font-heading text-xl font-black leading-tight text-white">Marstick</p><p className="text-xs leading-snug text-cyan-100/90">{publicCopy.common.sailSmarter}</p></div>}
          </div>
        </div>

        <div ref={menuScrollRef} className={cn("min-h-0 flex-1 overflow-y-auto", isRTL ? "pl-1" : "pr-1")}>
          <SmartMenu
            key={`public-menu-${lang}`}
            userState={userState}
            lang={lang}
            collapsed={collapsed}
            mode="public"
            onNavigate={onNavigate}
            onPremiumClick={onPremiumClick}
          />
        </div>

        {!collapsed && (
          <div className="shrink-0 rounded-3xl border border-primary/20 bg-primary/10 p-4 text-slate-950 shadow-sm dark:text-cyan-50">
            <p className="text-sm font-black">{publicCopy.common.premiumCtaTitle}</p>
            <p className="mt-1 text-xs text-slate-600 dark:text-cyan-50/80">{publicCopy.common.premiumCtaText}</p>
            <Button onClick={onPremiumClick} className="mt-3 w-full">{publicCopy.common.upgradeToPremium || publicCopy.common.upgrade || ""}</Button>
          </div>
        )}
      </div>
    </aside>
  );
}